<script setup>
import dayjs from 'dayjs'
import { computed } from 'vue'

/**
 * The component's inherited properties.
 */
const props = defineProps({
    /**
     * The given load.
     *
     * @type {Object}
     */
    value: {
        type: Object,
        required: true,
    },
})

/**
 * Determine if the load has a drive remaining.
 *
 * @return {Boolean}
 */
const hasDriveRemaining = computed(() => {
    return props.value?.drive_remaining !== null
})

/**
 * Determine if the load has a duty remaining.
 *
 * @return {Boolean}
 */
const hasDutyRemaining = computed(() => {
    return props.value?.on_duty_remaining !== null
})

/**
 * Determine if the load has a duty status.
 *
 * @return {Boolean}
 */
const hasDutyStatus = computed(() => {
    return !!props.value?.duty_status
})

/**
 * Determine if the load has hours of service.
 *
 * @return {Boolean}
 */
const hasHos = computed(() => {
    return !!props.value?.updated_at
})

/**
 * The on duty remaining time.
 *
 * @return {String}
 */
const onDutyRemainingTime = computed(() => {
    const dutyRemaining = Math.abs(props.value?.on_duty_remaining)

    const hours = Math.floor(dutyRemaining / 60)
    const remainingMinutes = dutyRemaining % 60
    const prefix = props.value?.on_duty_remaining < 0 ? '-' : ''

    const time = dayjs()
        .startOf('day')
        .add(hours, 'hour')
        .add(remainingMinutes, 'minute')
        .format('H:mm')

    return `${prefix}${time}`
})

/**
 * The remaining time.
 *
 * @return {String}
 */
const remainingTime = computed(() => {
    const driveRemaining = Math.abs(props.value?.drive_remaining)

    const hours = Math.floor(driveRemaining / 60)
    const remainingMinutes = driveRemaining % 60
    const prefix = props.value?.drive_remaining < 0 ? '-' : ''

    const time = dayjs()
        .startOf('day')
        .add(hours, 'hour')
        .add(remainingMinutes, 'minute')
        .format('H:mm')

    return `${prefix}${time}`
})
</script>

<template>
    <div v-if="hasHos" class="flex items-center">
        <span v-if="hasDriveRemaining" data-test="hos-remaining-time">
            {{ remainingTime }}
        </span>
        <div v-if="hasDutyRemaining" class="mx-1">/</div>
        <span v-if="hasDutyRemaining" data-test="hos-duty-remaining-time">
            {{ onDutyRemainingTime }}
        </span>
        <v-chip
            v-if="hasDutyStatus"
            color="primary"
            class="ml-2"
            size="x-small"
            data-test="hos-duty-status"
        >
            {{ value?.duty_status }}
        </v-chip>
    </div>
</template>
